import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Car, Lock, ShieldCheck } from 'lucide-react';
import { getVehicle, imgSrc } from '@/api/auction';
import { OfferDialog } from '@/components/auction/OfferDialog';
import { at, sourceLabel, statusLabel, vatLabel } from '@/i18n/auctionI18n';

const SPEC_ROWS = (v, lang) => [
  [at(lang, 'spec_lot'), v.lot_no], [at(lang, 'spec_year'), v.year], [at(lang, 'spec_make'), v.make], [at(lang, 'spec_model'), v.model],
  [at(lang, 'spec_mileage'), `${Number(v.mileage_km).toLocaleString()} ${at(lang, 'km')}`], [at(lang, 'spec_colour'), v.colour], [at(lang, 'spec_transmission'), v.transmission],
  [at(lang, 'spec_fuel'), v.fuel], [at(lang, 'spec_vin'), v.vin], [at(lang, 'spec_grade'), v.grade], [at(lang, 'spec_source'), sourceLabel(lang, v.source, v.source_label)],
  [at(lang, 'spec_vat'), vatLabel(lang, v.vat_treatment, v.vat_label)], [at(lang, 'spec_availability'), statusLabel(lang, v.availability_status, v.status_label)],
];

export default function AuctionLotPage({ lang = 'en' }) {
  const isAr = lang === 'ar';
  const base = isAr ? '/ar/auction' : '/auction';
  const BackIcon = isAr ? ChevronRight : ChevronLeft;
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [err, setErr] = useState(false);

  useEffect(() => {
    getVehicle(id).then((data) => { setV(data); document.title = `${data.year} ${data.make} ${data.model} | ${isAr ? 'مزاد يوروجلف' : 'Eurogulf Auction'}`; }).catch(() => setErr(true));
  }, [id, isAr]);

  const specs = useMemo(() => (v ? SPEC_ROWS(v, lang).filter(([, val]) => val) : []), [v, lang]);

  if (err) return <div dir={isAr ? 'rtl' : 'ltr'} className="min-h-[60vh] flex flex-col items-center justify-center gap-4"><p className="font-heading font-bold text-xl text-[#121212]">{at(lang, 'lotNotFound')}</p><Link to={base} className="btn-primary">{at(lang, 'backToCatalogue')}</Link></div>;
  if (!v) return <div className="min-h-[60vh] flex items-center justify-center"><p className="font-body text-[#666]">{at(lang, 'loadingLot')}</p></div>;

  const images = v.images || [];

  return (
    <div data-testid="auction-lot-page" dir={isAr ? 'rtl' : 'ltr'} className="bg-[#FAFAFA] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to={base} className="inline-flex items-center gap-1 font-body text-sm text-[#666666] hover:text-[#121212]"><BackIcon className="w-4 h-4" /> {at(lang, 'backToCatalogue')}</Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-[#EDEDEA] flex items-center justify-center">
              {images.length ? (
                <img data-testid="lot-main-image" src={imgSrc(images[active])} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" />
              ) : (
                <Car className="w-16 h-16 text-[#C4C4BE]" />
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                {images.map((src, i) => (
                  <button key={src} type="button" onClick={() => setActive(i)} className={`h-16 w-24 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${i === active ? 'border-[#EE5A01]' : 'border-transparent'}`}>
                    <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Specs */}
            <div className="mt-8 rounded-2xl border border-black/10 bg-white p-6">
              <h2 className="font-heading font-bold text-lg text-[#121212]">{at(lang, 'specifications')}</h2>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {specs.map(([k, val]) => (
                  <div key={k} className="flex justify-between border-b border-black/5 pb-2 gap-4">
                    <dt className="font-body text-sm text-[#666666]">{k}</dt>
                    <dd className={`font-heading font-bold text-sm text-[#121212] ${isAr ? 'text-left' : 'text-right'}`}>{val}</dd>
                  </div>
                ))}
              </dl>
              {(v.accident_history || v.service_history || v.description) && (
                <div className="mt-6 space-y-4">
                  {v.description && <div><h3 className="font-heading font-bold text-sm text-[#121212]">{at(lang, 'description')}</h3><p className="mt-1 font-body text-sm text-[#666666] leading-relaxed">{v.description}</p></div>}
                  {v.service_history && <div><h3 className="font-heading font-bold text-sm text-[#121212]">{at(lang, 'serviceHistory')}</h3><p className="mt-1 font-body text-sm text-[#666666]">{v.service_history}</p></div>}
                  {v.accident_history && <div><h3 className="font-heading font-bold text-sm text-[#121212]">{at(lang, 'accidentHistory')}</h3><p className="mt-1 font-body text-sm text-[#666666]">{v.accident_history}</p></div>}
                </div>
              )}
            </div>
          </div>

          {/* Offer sidebar */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
              <span className="font-mono text-xs text-[#EE5A01]">{v.lot_no}</span>
              <h1 className="mt-1 font-heading font-black text-2xl sm:text-3xl text-[#121212] tracking-tight leading-tight">{v.year} {v.make} {v.model}</h1>
              <div className="mt-4 flex flex-wrap gap-2">
                {v.source_label && <span className="font-body text-xs text-[#333] bg-[#F5F5F3] px-2.5 py-1 rounded-full">{sourceLabel(lang, v.source, v.source_label)}</span>}
                {v.vat_label && <span className="font-body text-xs text-[#333] bg-[#F5F5F3] px-2.5 py-1 rounded-full">{vatLabel(lang, v.vat_treatment, v.vat_label)}</span>}
                {v.status_label && <span className="font-body text-xs text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-full">{statusLabel(lang, v.availability_status, v.status_label)}</span>}
              </div>
              <div className="mt-5 rounded-xl bg-[#FAFAFA] border border-black/5 p-4 flex items-start gap-3">
                <Lock className="w-4 h-4 text-[#EE5A01] mt-0.5 flex-shrink-0" />
                <p className="font-body text-xs text-[#666666] leading-relaxed">{at(lang, 'pricingConfidential')}</p>
              </div>
              <div className="mt-5"><OfferDialog vehicle={v} lang={lang} /></div>
              <p className="mt-4 font-body text-[11px] text-[#888] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {at(lang, 'fleetMaintained')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
